import * as THREE from 'three';

import { FLARE_FRAG, FLARE_POSITION_FRAG, FLARE_VERT } from './orbShaders';
import { tintColor, tintVector } from './tint';

/**
 * 구 둘레의 섬광 — 원본 `LandingOrbFlareParticles` 이식.
 *
 * <h2>본체 입자와 «완전히 다른» 물건이다</h2>
 * 처음엔 본체 입자 시뮬을 그대로 돌리고 색만 바꿨는데, 원본은 이쪽에 <b>속도 시뮬이
 * 아예 없다</b>. 위치 판 하나뿐이고 셰이더도 따로다. 그래서 내 판은 「한 번 나오고 끝」
 * 이었다 — 본체의 `uDecay2 .01` 을 물려받아 수명이 100 초였기 때문이다.
 *
 * <h2>고리에서 태어나 바깥으로 뻗고 1.7 초 만에 죽는다</h2>
 * 초기 위치는 구면이 아니라 <b>XY 평면의 반지름 2.9 고리</b>다(z 는 ±0.1 만 흔든다).
 * 거기서 바깥으로 뻗어 나가고, 수명이 `uDecay .131` 로 프레임당 약 0.01 씩 줄어
 * <b>1.7 초 만에 소진</b>된 뒤 고리에서 다시 태어난다. 끊임없이 뿜었다 사라지는
 * 코로나가 이 순환이다.
 *
 * <p>보이는 구간도 좁다 — 정점 셰이더가 갓 태어난 것(life &gt; 0.8)과 사그라드는 것
 * (life &lt; 0.4)을 지우고, `uCenterFade(3, 3.3)` 가 반지름 3 안쪽을 지운다. 2.9 에서
 * 태어나므로 <b>구 밖으로 나온 뒤에야</b> 보인다.
 *
 * <p>속도는 입자마다 다르다 — 각도에 1D 값 노이즈를 물려 `0.2‥1.25` 배로 흩는다.
 * 그래서 코로나가 고르지 않고 가시처럼 뻗는다.
 */

/** `textureWidth: 96` → 96×96 = 9,216 개. `planeScale: .1` · `sizeRange [.5, 1]`. */
const FBO = 96;
const PLANE_SCALE = 0.1;
const SIZE_RANGE: [number, number] = [0.5, 1];
/** 고리 반지름과 z 흔들림. */
const RING_RADIUS = 2.9;
const RING_Z = 0.1;

/** 씬 파일이 덮어쓴 값. 나머지는 재질 기본값이다. */
const SIM = { uDecay: 0.131, uLerpSpeed: 0.15699999999999986, uFluidStrength: 0.03 };

const DRAW = {
  uParticleSize: 0.3,
  uColor: new THREE.Color(1, 0, 0),
  uBloomColor: new THREE.Vector3(0.5399999999999996, 0.9299999999999997, 0.94),
  uCenterFade: new THREE.Vector2(3, 3.3),
  uScaleRange: new THREE.Vector2(0.5, 1.2),
};

/** 원본 `Cce` — 값 256 개를 smoothstep 으로 잇는 1D 값 노이즈. */
function valueNoise1D() {
  const table: number[] = [];
  for (let i = 0; i < 256; i++) table.push(Math.random());
  return (v: number) => {
    const i = Math.floor(v);
    const f = v - i;
    const s = f * f * (3 - 2 * f);
    const a = table[i & 255]!;
    const b = table[(i + 1) & 255]!;
    return a * (1 - s) + b * s;
  };
}

/** 원본 `createParticlePositionData()` 그대로. `w` 는 입자별 속도 배수다. */
function ringTexture() {
  const total = FBO * FBO;
  const data = new Float32Array(total * 4);
  const noise = valueNoise1D();
  for (let i = 0; i < total; i++) {
    const o = Math.random();
    const a = (i / total) * Math.PI * 2;
    data[i * 4] = Math.cos(a) * RING_RADIUS;
    data[i * 4 + 1] = Math.sin(a) * RING_RADIUS;
    data[i * 4 + 2] = Math.sin(o * Math.PI * 2) * RING_Z;
    data[i * 4 + 3] = 0.2 + noise(a * Math.PI * 7) * (1.25 - 0.2);
  }
  const tex = new THREE.DataTexture(data, FBO, FBO, THREE.RGBAFormat, THREE.FloatType);
  tex.needsUpdate = true;
  return tex;
}

function pingPong() {
  const opts = {
    type: THREE.FloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    depthBuffer: false,
    stencilBuffer: false,
  } as const;
  const st = {
    read: new THREE.WebGLRenderTarget(FBO, FBO, opts),
    write: new THREE.WebGLRenderTarget(FBO, FBO, opts),
    swap() {
      const t = st.read;
      st.read = st.write;
      st.write = t;
    },
  };
  return st;
}

const SIM_VERT = 'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}';

export type OrbFlareOptions = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  fluidTexture: () => THREE.Texture;
  /** 주면 섬광 색을 이 계열로 옮긴다(`tintColor`). 본체 알갱이와 같은 값을 준다. */
  tint?: THREE.Color | null;
};

export function createOrbFlare(o: OrbFlareOptions) {
  const { renderer, scene, camera, tint } = o;

  const origin = ringTexture();
  const position = pingPong();

  const simScene = new THREE.Scene();
  const simCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const simGeo = new THREE.PlaneGeometry(2, 2);
  const simQuad = new THREE.Mesh(simGeo);
  simScene.add(simQuad);

  const simUniforms = {
    tOriginalTexture: { value: origin },
    tTexture: { value: position.read.texture },
    tFluidSim: { value: o.fluidTexture() },
    uTime: { value: 0 },
    uNormalizedDelta: { value: 1 },
    uIntroAnimation: { value: 1 },
    uDecay: { value: SIM.uDecay },
    uLerpSpeed: { value: SIM.uLerpSpeed },
    uFluidStrength: { value: SIM.uFluidStrength },
    uSetup: { value: 1 },
    uModelMatrix: { value: new THREE.Matrix4() },
    uViewMatrix: { value: camera.matrixWorldInverse },
    uProjectionMatrix: { value: camera.projectionMatrix },
  };
  const simMat = new THREE.ShaderMaterial({
    vertexShader: SIM_VERT,
    fragmentShader: FLARE_POSITION_FRAG,
    uniforms: simUniforms,
    depthTest: false,
    depthWrite: false,
  });

  const count = FBO * FBO;
  const quad = new THREE.PlaneGeometry(PLANE_SCALE, PLANE_SCALE);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = quad.index;
  geo.attributes.position = quad.attributes.position!;
  geo.attributes.uv = quad.attributes.uv!;
  geo.attributes.normal = quad.attributes.normal!;
  geo.instanceCount = count;

  const fboUv = new Float32Array(count * 2);
  const random = new Float32Array(count * 3);
  const scale = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    fboUv[i * 2] = ((i % FBO) + 0.5) / FBO;
    fboUv[i * 2 + 1] = (Math.floor(i / FBO) + 0.5) / FBO;
    random[i * 3] = Math.random();
    random[i * 3 + 1] = Math.random();
    random[i * 3 + 2] = Math.random();
    scale[i] = SIZE_RANGE[0] + Math.random() * (SIZE_RANGE[1] - SIZE_RANGE[0]);
  }
  geo.setAttribute('aFboUv', new THREE.InstancedBufferAttribute(fboUv, 2));
  geo.setAttribute('aRandom', new THREE.InstancedBufferAttribute(random, 3));
  geo.setAttribute('aScale', new THREE.InstancedBufferAttribute(scale, 1));

  const drawUniforms = {
    tPosition: { value: position.read.texture },
    uTime: { value: 0 },
    uOpacity: { value: 1 },
    uParticleSize: { value: DRAW.uParticleSize },
    uScaleRange: { value: DRAW.uScaleRange.clone() },
    uCenterFade: { value: DRAW.uCenterFade.clone() },
    /* 섬광도 알갱이와 같은 계열로 따라간다. 본체만 물들이고 여기를 두면 구 둘레에
       원본 색(붉은 알·청록 번짐)이 따로 돈다. */
    uColor: { value: tint ? tintColor(DRAW.uColor, tint) : DRAW.uColor.clone() },
    uBloomColor: { value: tint ? tintVector(DRAW.uBloomColor, tint) : DRAW.uBloomColor.clone() },
  };
  const mesh = new THREE.Mesh(
    geo,
    new THREE.ShaderMaterial({
      vertexShader: FLARE_VERT,
      fragmentShader: FLARE_FRAG,
      uniforms: drawUniforms,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      // 원본 material.blending "2" = AdditiveBlending.
      blending: THREE.AdditiveBlending,
    }),
  );
  mesh.frustumCulled = false;
  scene.add(mesh);

  let first = true;
  const update = (elapsed: number, deltaMs: number) => {
    simUniforms.uTime.value = elapsed;
    simUniforms.uNormalizedDelta.value = Math.min(2, deltaMs / 16.666);
    simUniforms.uSetup.value = first ? 1 : 0;
    simUniforms.tFluidSim.value = o.fluidTexture();
    simUniforms.uModelMatrix.value.copy(mesh.matrixWorld);
    simUniforms.tTexture.value = position.read.texture;

    simQuad.material = simMat;
    renderer.setRenderTarget(position.write);
    renderer.render(simScene, simCamera);
    renderer.setRenderTarget(null);
    position.swap();

    drawUniforms.tPosition.value = position.read.texture;
    drawUniforms.uTime.value = elapsed;
    first = false;
  };

  const dispose = () => {
    scene.remove(mesh);
    geo.dispose();
    quad.dispose();
    simGeo.dispose();
    (mesh.material as THREE.Material).dispose();
    simMat.dispose();
    position.read.dispose();
    position.write.dispose();
    origin.dispose();
  };

  /** 본체 알갱이와 같은 자리(`OrbParticles.setTint`). 둘을 늘 같은 값으로 부른다. */
  const setTint = (next: THREE.Color | null) => {
    drawUniforms.uColor.value.copy(next ? tintColor(DRAW.uColor, next) : DRAW.uColor);
    drawUniforms.uBloomColor.value.copy(next ? tintVector(DRAW.uBloomColor, next) : DRAW.uBloomColor);
  };

  return { mesh, update, dispose, setTint };
}
